import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ResearchLayout from '../components/ResearchLayout';
import api from '../services/api';
import useDashboardPreferences from '../hooks/useDashboardPreferences';

const StatisticianDashboard = () => {
  const { user } = useAuth();
  const { preferences } = useDashboardPreferences();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [doctors, setDoctors] = useState([]);
  
  const [stats, setStats] = useState({
    totalResponses: 0,
    completedCases: 0,
    averageScore: 0,
    activeProtocols: 0
  });
  const [healthDistribution, setHealthDistribution] = useState({});

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load dashboard overview statistics
      try {
        const statsRes = await api.get('/statistics/overview');
        if (statsRes.data.success) {
          setStats(statsRes.data.statistics);
          setHealthDistribution(statsRes.data.statistics.healthDistribution || {});
        }
      } catch (err) {
        console.error('Failed to fetch statistics overview:', err);
        setError(prevError => prevError ? 
          `${prevError}; Failed to load statistics` : 
          'Failed to load statistics');
      }
      
      // Fetch protocols
      try {
        const protocolsRes = await api.get('/protocols');
        const protocolsData = protocolsRes.data.protocols || [];
        setProtocols(protocolsData);
      } catch (err) {
        console.error('Failed to fetch protocols:', err);
        setError(prevError => prevError ? 
          `${prevError}; Failed to load protocols` : 
          'Failed to load protocols');
      }
      
      // Fetch doctors
      try {
        const usersRes = await api.get('/users?role=doctor');
        const doctorsData = usersRes.data.users || [];
        setDoctors(doctorsData);
      } catch (err) {
        console.error('Failed to fetch doctors:', err);
        setError(prevError => prevError ? 
          `${prevError}; Failed to load doctors` : 
          'Failed to load doctors');
      }
      
      // Fetch assessments
      try {
        const assessmentsRes = await api.get('/assessments');
        const assessmentsData = assessmentsRes.data.assessments || [];
        setAssessments(assessmentsData);
      } catch (err) {
        console.error('Failed to fetch assessments:', err);
        setError(prevError => prevError ? 
          `${prevError}; Failed to load assessments` : 
          'Failed to load assessments');
      }
      
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data. Some information may be incomplete.');
    } finally {
      setLoading(false);
    }
  };

  const getHealthColorClass = (prediction) => {
    switch (prediction?.toLowerCase()) {
      case 'excellent': return 'text-success';
      case 'good': return 'text-info';
      case 'fair': return 'text-warning';
      case 'at risk':
      case 'needs attention':
      case 'high risk':
      case 'critical': return 'text-danger';
      default: return 'text-muted';
    }
  };

  const getScoreBadgeClass = (score) => {
    if (score >= 80) return 'bg-success';
    if (score >= 60) return 'bg-warning';
    return 'bg-danger';
  };
  
  // Check if role-specific sections should be shown
  const shouldShowSection = (sectionKey) => {
    const roleSpecificSections = preferences.role_specific_sections || {};
    return roleSpecificSections[sectionKey] !== false; // Default to true if not specified
  };

  const mapColorToHealthStatus = (colorCode) => {
    const colorMap = {
      "#83D65C": "Excellent",
      "#5BC0DE": "Good",
      "#ffe366": "Good",
      "#ff944d": "Fair", 
      "#ff6666": "High Risk",
      "#d9534f": "Critical"
    };
    
    return colorMap[colorCode] || colorCode;
  };

  if (loading) {
    return (
      <ResearchLayout pageTitle="Statistician Dashboard">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  return (
    <ResearchLayout pageTitle="Statistician Dashboard">
      <div className="container-fluid">
        {/* Welcome Section - conditionally rendered based on preferences */}
        {preferences.welcome_section_visible && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm bg-gradient-info text-white">
                <div className="card-body" style={{backgroundColor: '#7B68EE'}}>
                  <h3 className="mb-2">Welcome back, {user?.name}!</h3>
                  <p className="mb-0 opacity-75">
                    Analyze research data, create reports, and generate insights from collected responses.
                  </p>
                  {error && (
                    <div className="alert alert-warning mt-3 mb-0">
                      <i className="fa fa-exclamation-triangle me-2"></i>
                      {error}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards - conditionally rendered based on preferences */}
        {preferences.stats_cards_visible && (
          <div className="row mb-4">
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-info mb-2">
                    <i className="fa fa-chart-line fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-info">{stats.totalResponses}</h4>
                  <p className="text-muted mb-0">Total Responses</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-success mb-2">
                    <i className="fa fa-check-circle fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-success">{stats.completedCases}</h4>
                  <p className="text-muted mb-0">Completed Cases</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-warning mb-2">
                    <i className="fa fa-calculator fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-warning">{stats.averageScore}</h4>
                  <p className="text-muted mb-0">Average Score</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-primary mb-2">
                    <i className="fa fa-clipboard-list fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-primary">{stats.activeProtocols}</h4>
                  <p className="text-muted mb-0">Active Protocols</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Health Risk Profile Distribution */}
        <div className="row mb-4">
          <div className="col-lg-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">
                    <i className="fa fa-chart-pie text-info me-2"></i>
                    Health Risk Profile Distribution
                  </h5>
                  <p className="text-muted small mb-0 mt-1">
                    Patient distribution across health categories
                  </p>
                </div>
                <span className="badge bg-light text-dark">
                  Total: {Object.values(healthDistribution).reduce((sum, val) => sum + val, 0)}
                </span>
              </div>
              <div className="card-body">
                {Object.entries(healthDistribution).length > 0 ? (
                  Object.entries(healthDistribution).map(([key, count]) => {
                    // Map color codes to health status labels
                    const prediction = mapColorToHealthStatus(key);
                    const colorClass = getHealthColorClass(prediction);
                    const percentage = Math.round((count / Object.values(healthDistribution).reduce((a, b) => a + b, 0)) * 100);
                    
                    return (
                      <div key={key} className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="fw-bold" style={{ color: key.startsWith('#') ? key : undefined }}>
                            {prediction}
                          </span>
                          <div>
                            <span className="badge bg-secondary me-2">{count} patients</span>
                            <span className="text-muted small">
                              {percentage}%
                            </span>
                          </div>
                        </div>
                        <div className="progress" style={{ height: '10px' }}>
                          <div
                            className={`progress-bar ${colorClass}`}
                            role="progressbar"
                            style={{ width: `${percentage}%` }}
                            aria-valuenow={percentage}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          ></div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-chart-pie fa-3x text-muted mb-3 opacity-50"></i>
                    <h6 className="text-muted">No Health Profile Data Available</h6>
                    <p className="text-muted small">
                      Complete patient assessments to generate risk profile data
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent High-Risk Cases */}
          <div className="col-lg-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom">
                <h5 className="mb-0">
                  <i className="fas fa-exclamation-triangle text-danger me-2"></i>
                  High-Risk Cases
                </h5>
              </div>
              <div className="card-body">
                {assessments
                  .filter(a => a.score < 60)
                  .slice(0, 5)
                  .map((case_, idx) => (
                    <div key={case_.id || idx} className="mb-3 p-3 border rounded">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <strong>Case ID: {case_.customerId || case_.id}</strong>
                          <div className="small text-muted">
                            {case_.physicalHealthPrediction || 'No prediction'}
                          </div>
                        </div>
                        <span className={`badge ${getScoreBadgeClass(case_.score || 0)}`}>
                          {case_.score || 0}/100
                        </span>
                      </div>
                      <div className="small text-muted">
                        AI Assessment: {case_.aiResponse ? `${case_.aiResponse.substring(0, 100)}...` : 'Not available'}
                      </div>
                    </div>
                  ))}
                
                {assessments.filter(a => a.score < 60).length === 0 && (
                  <p className="text-muted text-center py-3">No high-risk cases found</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Data Overview - specific for statisticians */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom">
                <h5 className="mb-0">
                  <i className="fas fa-database text-info me-2"></i>
                  Data Overview
                </h5>
              </div>
              <div className="card-body">
                <div className="row text-center">
                  <div className="col-md-2 mb-3">
                    <div className="h4 text-primary">{assessments.length}</div>
                    <div className="text-muted small">Total Cases</div>
                  </div>
                  <div className="col-md-2 mb-3">
                    <div className="h4 text-success">
                      {assessments.filter(a => a.isSmoker === false).length}
                    </div>
                    <div className="text-muted small">Non-Smokers</div>
                  </div>
                  <div className="col-md-2 mb-3">
                    <div className="h4 text-info">
                      {assessments.filter(a => a.activeDailyLifestyleLevel === 'High').length}
                    </div>
                    <div className="text-muted small">High Activity</div>
                  </div>
                  <div className="col-md-2 mb-3">
                    <div className="h4 text-warning">
                      {assessments.filter(a => 
                        a.diagnosedChronicDisease && 
                        a.diagnosedChronicDisease !== 'None'
                      ).length}
                    </div>
                    <div className="text-muted small">Chronic Disease</div>
                  </div>
                  <div className="col-md-2 mb-3">
                    <div className="h4 text-success">
                      {assessments.length > 0 
                        ? Math.round(assessments.reduce((sum, a) => 
                            sum + (a.assessmentCompletionPercentage || 0), 0
                          ) / assessments.length)
                        : 0}%
                    </div>
                    <div className="text-muted small">Avg Completion</div>
                  </div>
                  <div className="col-md-2 mb-3">
                    <div className="h4 text-primary">
                      {doctors.length}
                    </div>
                    <div className="text-muted small">Active Doctors</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions - conditionally rendered based on preferences */}
        {preferences.quick_actions_visible && (
          <div className="row">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0">
                    <i className="fas fa-bolt text-info me-2"></i>
                    Quick Actions
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-lg-3 col-md-6 mb-3">
                      <Link to="/reports/create" className="btn btn-outline-primary w-100 p-3">
                        <i className="fas fa-chart-bar fa-2x mb-2"></i>
                        <br />
                        Create Report
                      </Link>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                      <Link to="/data/export" className="btn btn-outline-success w-100 p-3">
                        <i className="fas fa-database fa-2x mb-2"></i>
                        <br />
                        Export Data
                      </Link>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                      <Link to="/analytics" className="btn btn-outline-info w-100 p-3">
                        <i className="fas fa-chart-line fa-2x mb-2"></i>
                        <br />
                        Analytics
                      </Link>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                      <Link to="/reports/generate-excel" className="btn btn-outline-warning w-100 p-3">
                        <i className="fas fa-file-excel fa-2x mb-2"></i>
                        <br />
                        Generate Excel
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ResearchLayout>
  );
};

export default StatisticianDashboard;